import React, { useContext, useState, useEffect } from "react";
import { CartContext } from "../components/CartContext";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";

const CheckoutPage = () => {
  const { cartItems, clearCart } = useContext(CartContext);
  const [promoCode, setPromoCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [message, setMessage] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [showAlert, setShowAlert] = useState({ visible: false, orderId: "" });

  const promoCodes = { SAVE10: 10, SAVE20: 20 };
  const { width, height } = useWindowSize();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ✅ Calculate subtotal, delivery, and total
  const subtotal = cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const deliveryFee = subtotal > 100 ? 0 : 50.0; // ✅ Added delivery fee condition
  const totalBeforeDiscount = subtotal + deliveryFee;
  const total =
    discount > 0
      ? totalBeforeDiscount - (totalBeforeDiscount * discount) / 100
      : totalBeforeDiscount;

  const applyPromo = () => {
    const code = promoCode.trim().toUpperCase();
    if (promoCodes[code]) {
      setDiscount(promoCodes[code]);
      setMessage(`Promo applied! ${promoCodes[code]}% off`);
    } else {
      setDiscount(0);
      setMessage("Invalid promo code");
    }
  };

  const handlePlaceOrder = () => {
    const inputs = document.querySelectorAll(".checkout-page input[required]");
    const emptyFields = Array.from(inputs).filter((input) => !input.value.trim());
    if (emptyFields.length > 0) {
      emptyFields[0].scrollIntoView({ behavior: "smooth", block: "center" });
      emptyFields.forEach((field) => {
        field.classList.add("is-invalid");
        setTimeout(() => field.classList.remove("is-invalid"), 2000);
      });
      alert("⚠️ Please fill out all required fields before placing your order.");
      return;
    }

    const orderId = "ORD-" + Math.floor(100000 + Math.random() * 900000);

    setShowAlert({ visible: true, orderId });
    clearCart();

    // Auto-hide overlay after 4s
    setTimeout(() => setShowAlert({ visible: false, orderId: "" }), 4000);
  };

  return (
    <motion.div
      className="checkout-page container py-5"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <motion.h2
          className="fw-bold"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          Checkout
        </motion.h2>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Link to="/cart" className="btn btn-outline-dark btn-sm">
            ← Back to Cart
          </Link>
        </motion.div>
      </div>

      {cartItems.length === 0 ? (
        <motion.div className="text-center py-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <h5>Your cart is empty.</h5>
        </motion.div>
      ) : (
        <div className="row g-4">
          {/* Billing & Payment */}
          <motion.div className="col-md-7">
            <div className="p-4 rounded shadow-sm bg-white">
              <h5 className="fw-semibold mb-3">Billing Details</h5>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">First Name</label>
                  <input type="text" className="form-control" required />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Last Name</label>
                  <input type="text" className="form-control" required />
                </div>
                <div className="col-12 mb-3">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-control" required />
                </div>
                <div className="col-12 mb-3">
                  <label className="form-label">Phone</label>
                  <input type="number" className="form-control" required />
                </div>
                <div className="col-12 mb-3">
                  <label className="form-label">Address</label>
                  <input type="text" className="form-control" required />
                </div>
                <div className="col-12 mb-3">
                  <label className="form-label">State</label>
                  <input type="text" className="form-control" required />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">City</label>
                  <input type="text" className="form-control" required />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Postal Code</label>
                  <input type="number" className="form-control" required />
                </div>
              </div>

              <div className="p-4 rounded shadow-sm bg-white mt-4">
                <h5 className="fw-semibold mb-3">Payment Method</h5>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="payment"
                    id="cod"
                    value="cod"
                    checked={paymentMethod === "cod"}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="cod">
                    Cash on Delivery
                  </label>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Order Summary */}
          <motion.div className="col-md-5">
            <div className="p-4 rounded shadow-sm bg-white">
              <h5 className="fw-semibold mb-3">Order Summary</h5>
              {cartItems.map((item, index) => (
                <div key={index} className="d-flex justify-content-between border-bottom pb-2 mb-2">
                  <div>
                    <strong>{item.name}</strong>
                    <br />
                    <small className="text-muted">
                      {item.category}
                      {item.subcategory ? ` / ${item.subcategory}` : ""}
                    </small>
                  </div>
                  <div>${item.price.toFixed(2)} × {item.quantity}</div>
                </div>
              ))}

              <div className="d-flex justify-content-between mt-3 mb-2">
                <span>Subtotal</span>
                <strong>${subtotal.toFixed(2)}</strong>
              </div>

              <div className="d-flex justify-content-between mb-2">
                <span>Delivery Fee</span>
                <strong>{deliveryFee === 0 ? "Free" : `$${deliveryFee.toFixed(2)}`}</strong>
              </div>

              {discount > 0 && (
                <div className="d-flex justify-content-between mb-2 text-success">
                  <span>Discount ({discount}%)</span>
                  <strong>-${((totalBeforeDiscount * discount) / 100).toFixed(2)}</strong>
                </div>
              )}

              <div className="d-flex justify-content-between border-top pt-3 mt-3">
                <h5 className="fw-bold">Total</h5>
                <h5 className="fw-bold">${total.toFixed(2)}</h5>
              </div>

              <div className="input-group mt-3 mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                />
                <button className="btn btn-dark" onClick={applyPromo}>Apply</button>
              </div>

              {message && (
                <small className={`d-block ${discount > 0 ? "text-success" : "text-danger"}`}>
                  {message}
                </small>
              )}

              <motion.button
                className="btn btn-dark w-100 mt-3 py-2 fw-semibold fs-5"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePlaceOrder}
              >
                Place Order – ${total.toFixed(2)}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Overlay with Confetti */}
      <AnimatePresence>
        {showAlert.visible && (
          <>
            <Confetti width={width} height={height} numberOfPieces={300} recycle={false} />
            <motion.div
              className="overlay d-flex justify-content-center align-items-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "rgba(0,0,0,0.5)",
                zIndex: 2000,
              }}
            >
              <motion.div
                className="alert-box bg-white rounded shadow p-5 text-center"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h4 className="fw-bold text-success mb-2">🎉 Order Placed Successfully!</h4>
                <p className="text-muted mb-2">Thank you for shopping with us.</p>
                <p className="fw-semibold">
                  Your Order ID: <span className="text-dark">{showAlert.orderId}</span>
                </p>
                <Link to="/">
                  <button className="btn btn-dark mt-3">Continue Shopping</button>
                </Link>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CheckoutPage;
